import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, CornerDownLeft } from 'lucide-react';
import { ALL_PALETTE_ITEMS } from '../config/nav';

function normalize(str) {
  return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export default function CommandPalette({ open, onClose }) {
  const [query, setQuery]   = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef            = useRef(null);
  const navigate            = useNavigate();

  const filtered = query.trim() === ''
    ? ALL_PALETTE_ITEMS
    : ALL_PALETTE_ITEMS.filter(item =>
        normalize(item.label).includes(normalize(query.trim()))
      );

  // Agrupar manteniendo orden: Acción rápida primero, luego Navegar
  const groups = filtered.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // pequeño delay para que el DOM esté listo
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  const go = useCallback((item) => {
    navigate(item.to);
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      if (filtered[cursor]) go(filtered[cursor]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [filtered, cursor, go, onClose]);

  if (!open) return null;

  let flatIdx = 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[11vh] px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/25 backdrop-blur-[3px]" onClick={onClose} />

      {/* Panel */}
      <div
        className="relative w-full max-w-[540px] bg-white rounded-2xl shadow-2xl shadow-slate-900/15 ring-1 ring-slate-900/8 overflow-hidden"
        style={{ animation: 'palette-in 140ms cubic-bezier(0.16,1,0.3,1)' }}
      >
        {/* Barra de búsqueda */}
        <div className="flex items-center gap-3 px-4 border-b border-slate-100">
          <Search size={15} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar menú o acción..."
            className="flex-1 text-[14px] text-slate-900 placeholder:text-slate-400 bg-transparent outline-none py-4"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-slate-300 hover:text-slate-500 transition-colors text-[11px] font-mono px-1"
            >
              ✕
            </button>
          )}
        </div>

        {/* Resultados */}
        <div className="max-h-[340px] overflow-y-auto overscroll-contain">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-300 select-none">
              <Search size={26} strokeWidth={1.5} />
              <p className="text-sm mt-2.5 text-slate-400">Sin resultados para &quot;{query}&quot;</p>
            </div>
          ) : (
            Object.entries(groups).map(([group, items]) => (
              <div key={group} className="py-1.5">
                <p className="px-4 pt-2 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest select-none">
                  {group}
                </p>
                {items.map((item) => {
                  const idx = flatIdx++;
                  const isActive  = idx === cursor;
                  const isAction  = item.group === 'Acción rápida';
                  return (
                    <button
                      key={item.to}
                      onMouseEnter={() => setCursor(idx)}
                      onClick={() => go(item)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-75 ${
                        isActive ? 'bg-primary-50' : ''
                      }`}
                    >
                      {/* Ícono */}
                      <span className={`flex items-center justify-center w-[26px] h-[26px] rounded-lg shrink-0 transition-colors ${
                        isActive
                          ? isAction ? 'bg-primary-500 text-white' : 'bg-primary-100 text-primary-600'
                          : isAction ? 'bg-primary-100 text-primary-500' : 'bg-slate-100 text-slate-400'
                      }`}>
                        <item.Icon size={13} strokeWidth={2} />
                      </span>

                      {/* Label */}
                      <span className={`text-[13.5px] font-medium flex-1 leading-none ${
                        isActive ? 'text-primary-800' : 'text-slate-700'
                      }`}>
                        {item.label}
                      </span>

                      {/* Indicador activo */}
                      {isActive && (
                        <ArrowRight size={13} className="text-primary-400 shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer de atajos */}
        <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-4 text-[11px] text-slate-300 select-none">
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-slate-50 px-1.5 py-0.5 rounded ring-1 ring-slate-200/80 text-slate-400">↑↓</kbd>
            navegar
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-slate-50 px-1.5 py-0.5 rounded ring-1 ring-slate-200/80 text-slate-400">
              <CornerDownLeft size={10} className="inline" />
            </kbd>
            abrir
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-slate-50 px-1.5 py-0.5 rounded ring-1 ring-slate-200/80 text-slate-400">esc</kbd>
            cerrar
          </span>
        </div>
      </div>

      <style>{`
        @keyframes palette-in {
          from { opacity: 0; transform: scale(0.97) translateY(-6px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
      `}</style>
    </div>
  );
}
